import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

import LoginModel from '../../../../../models/user.model';
import { AppCommon } from '../../../../../providers/app-common';
import { SourcingNeedV2ViewModel } from '../../../../../models-views/sourcing-needs-v2.view.model';
import { StandardResponseModel } from '../../../../../models/project.model';

@Component({
  selector: 'app-sn-form-branding-v2',
  templateUrl: './sn-form-branding-v2.component.html',
  styleUrls: ['./sn-form-branding-v2.component.scss'],
  standalone: false
})
export class SnFormBrandingV2Component implements OnInit, OnChanges {
  @Input() eventId: number | null = null;
  @Input() data: any;
  @Output() closeModal = new EventEmitter<'success' | 'cancel'>();
  
  form!: FormGroup;
  isEdit: boolean = true;
  isView: boolean = false;
  existingData: any = null;

  stageAccessoriesResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  guestManagementResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  signageBrandingResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  demoEngagementResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  mediaAdvertisingResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());
  setupBrandingResponseModel = new BehaviorSubject<StandardResponseModel>(new StandardResponseModel());

  private readonly stageSizePresets: Record<string, { x: number; y: number }> = {
    small: { x: 8, y: 8 },
    medium: { x: 12, y: 8 },
    large: { x: 16, y: 8 },
  };

  constructor(
    private fb: FormBuilder, 
    private vm: SourcingNeedV2ViewModel,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.vm.parentSnFormBrandingV2Component = this;
    this.initForm();
    this.setupStageFieldLogic();
    this.vm.subscribeToBrandingModels(
      this.stageAccessoriesResponseModel,
      this.guestManagementResponseModel,
      this.signageBrandingResponseModel,
      this.demoEngagementResponseModel,
      this.mediaAdvertisingResponseModel,
      this.setupBrandingResponseModel
    );
    this.vm.initGeneralCallsForBrandings();
    
    setTimeout(() => {
      this.handleInputData();
    }, 300);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.handleInputData();
    }
  }

  handleInputData(): void {
    if (this.data) {
      this.existingData = this.data;
      this.isView = false;
      this.isEdit = true;
      this.form.patchValue(this.mapApiToForm(this.data));
    } else {
      this.existingData = null;
      this.isView = false;
      this.isEdit = true;
      this.form.reset();
      this.form.enable();
      this.cdr.detectChanges();
    }
  }

  mapApiToForm(data: any): any {
    const isStageRequired = !!data.is_stage_required;
    const stageX = data.stage_dimension_x ?? null;
    const stageY = data.stage_dimension_y ?? null;

    // Map API response to form fields - extract IDs from details arrays
    return {
      is_stage_required: isStageRequired,
      stage_size_option: this.getStageSizeOption(isStageRequired, stageX, stageY),
      stage_dimension_x: stageX,
      stage_dimension_y: stageY,
      stage_accessories: data.stage_accessories_details?.map((item: any) => item.id) || [],
      guest_management: data.guest_management_details?.map((item: any) => item.id) || [],
      signage_branding: data.signage_branding_details?.map((item: any) => item.id) || [],
      demo_engagement: data.demo_engagement_details?.map((item: any) => item.id) || [],
      media_advertising: data.media_advertising_details?.map((item: any) => item.id) || [],
      setup_branding: data.setup_branding_details?.map((item: any) => item.id) || [],
    };
  }

  initForm(): void {
    this.form = this.fb.group({
      is_stage_required: [false],
      stage_size_option: [null],
      stage_dimension_x: [null],
      stage_dimension_y: [null],
      stage_accessories: [[],],
      guest_management: [[],],
      signage_branding: [[],],
      demo_engagement: [[],],
      media_advertising: [[],],
      setup_branding: [[],],
    });
  }

  onSave(): void {
    if (this.form.valid) {
      const payload = this.form.value;
      delete payload.stage_size_option;

      if (!payload.is_stage_required) {
        payload.stage_dimension_x = null;
        payload.stage_dimension_y = null;
      } else {
        payload.stage_dimension_x = payload.stage_dimension_x != null ? Number(payload.stage_dimension_x) : null;
        payload.stage_dimension_y = payload.stage_dimension_y != null ? Number(payload.stage_dimension_y) : null;
      }

      payload.sourcing_need = this.eventId;
      payload.is_branding_collateral_required = true;
      
      const loggedInUser = AppCommon.getloggedInObject();
      const userId = loggedInUser?.user?.id;

      payload.stage_accessories ??= [];
      payload.guest_management ??= [];
      payload.signage_branding ??= [];
      payload.demo_engagement ??= [];
      payload.media_advertising ??= [];
      payload.setup_branding ??= [];
      
      if (this.isEdit && this.existingData) {
        payload.id = this.existingData.id;
        payload.modified_by = userId;
        this.vm.addUpdateSouringBranding(payload, true);
      } else {
        payload.created_by = userId;
        this.vm.addUpdateSouringBranding(payload, false);
      }
    }
  }

  sourcingBrandingAdded() {
    this.closeModal.emit('success');
    
    // Refresh parent component data
    if (this.eventId) {
      this.vm.getSouringNeedbyId(this.eventId, 'cet-sourcing-needs-v2');
    }
  }

  onEdit(): void {
    this.isEdit = true;
    this.isView = false;
    this.form.enable();
  }

  onCancel(): void {
    this.closeModal.emit('cancel');
  }

  private setupStageFieldLogic(): void {
    this.form.get('is_stage_required')?.valueChanges.subscribe((required) => {
      this.onStageRequiredChange(!!required);
    });

    this.form.get('stage_size_option')?.valueChanges.subscribe((stageSize) => {
      this.onStageSizeChange(stageSize);
    });

    this.onStageRequiredChange(!!this.form.get('is_stage_required')?.value);
  }

  private onStageRequiredChange(required: boolean): void {
    const stageSizeControl = this.form.get('stage_size_option');
    const xControl = this.form.get('stage_dimension_x');
    const yControl = this.form.get('stage_dimension_y');

    if (!stageSizeControl || !xControl || !yControl) return;

    if (!required) {
      stageSizeControl.setValue(null, { emitEvent: false });
      stageSizeControl.clearValidators();
      xControl.setValue(null, { emitEvent: false });
      yControl.setValue(null, { emitEvent: false });
      xControl.clearValidators();
      yControl.clearValidators();
    } else {
      stageSizeControl.setValidators([Validators.required]);
      this.onStageSizeChange(stageSizeControl.value);
    }

    stageSizeControl.updateValueAndValidity({ emitEvent: false });
    xControl.updateValueAndValidity({ emitEvent: false });
    yControl.updateValueAndValidity({ emitEvent: false });
  }

  private onStageSizeChange(stageSize: string | null): void {
    const required = !!this.form.get('is_stage_required')?.value;
    const xControl = this.form.get('stage_dimension_x');
    const yControl = this.form.get('stage_dimension_y');

    if (!xControl || !yControl) return;

    if (!required) {
      xControl.clearValidators();
      yControl.clearValidators();
      xControl.setValue(null, { emitEvent: false });
      yControl.setValue(null, { emitEvent: false });
      xControl.updateValueAndValidity({ emitEvent: false });
      yControl.updateValueAndValidity({ emitEvent: false });
      return;
    }

    if (stageSize && this.stageSizePresets[stageSize]) {
      const preset = this.stageSizePresets[stageSize];
      xControl.clearValidators();
      yControl.clearValidators();
      xControl.setValue(preset.x, { emitEvent: false });
      yControl.setValue(preset.y, { emitEvent: false });
    } else if (stageSize === 'custom') {
      xControl.setValidators([Validators.required, Validators.min(1)]);
      yControl.setValidators([Validators.required, Validators.min(1)]);
      xControl.setValue(xControl.value, { emitEvent: false });
      yControl.setValue(yControl.value, { emitEvent: false });
    } else {
      xControl.clearValidators();
      yControl.clearValidators();
      xControl.setValue(null, { emitEvent: false });
      yControl.setValue(null, { emitEvent: false });
    }

    xControl.updateValueAndValidity({ emitEvent: false });
    yControl.updateValueAndValidity({ emitEvent: false });
  }

  private getStageSizeOption(isStageRequired: boolean, x: number | null, y: number | null): string | null {
    if (!isStageRequired) return null;

    const entries = Object.entries(this.stageSizePresets);
    for (const [key, preset] of entries) {
      if (Number(x) === preset.x && Number(y) === preset.y) {
        return key;
      }
    }

    return x != null && y != null ? 'custom' : null;
  }
}